import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { getPricebookCustomers } from "../../../../services";
import { PricebookCustomer } from "../../../../types/price-book.type";
import { featureAccess } from "../../../../permissions/access";
import { useUserRole } from "../../../../utils/hooks/useUserRole";
import { numberWithCommas } from "../../../../utils";
import { customerDetailsPath } from "../../../Customer/data";
import Skeleton from "../../../ReprintAuth/Skeleton";
import CataloguePagination from "../../shared/Pagination";
import { useSearchablePagedRows } from "../../shared/useSearchablePagedRows";
import {
  CATALOGUE_CELL_PADDING,
  STRIPE_BACKGROUND,
  catalogueRowHeight,
} from "../../shared/constants";

/** Every cell holds a single line of text, which is what sets the row height. */
const ROW_TEXT_HEIGHT = 22;
const ROW_HEIGHT = catalogueRowHeight(ROW_TEXT_HEIGHT);

const BASE_COLUMNS = ["รหัสลูกค้า", "ชื่อลูกค้า", "กลุ่มลูกค้า"];
const BRANCH_COLUMN = "สาขา";

const EMPTY_PLACEHOLDER = "-";

/** Same "code - name" pairing the branch pickers use; either half may be missing. */
const formatBranch = (customer: PricebookCustomer): string =>
  [customer.branch_code, customer.branch_name]
    .filter(Boolean)
    .join(" - ") || EMPTY_PLACEHOLDER;

interface CustomerListModalProps {
  open: boolean;
  priceBookUuid: string;
  /** Narrows the list to one branch's shops; omitted, every shop the reader may see. */
  branchUuid?: string;
  /** "code name" of that branch, for the title. */
  branchLabel?: string;
  onClose: () => void;
}

/**
 * Read-only list of every customer assigned to one pricebook.
 *
 * The pricebook screen no longer assigns customers — that happens on the customer page —
 * so this is a way to read the list and to reach any one of those customers, nothing more.
 */
export default function CustomerListModal({
  open,
  priceBookUuid,
  branchUuid,
  branchLabel,
  onClose,
}: CustomerListModalProps) {
  const role = useUserRole();
  // A role answered with the stores of every branch is the only one whose list needs to say
  // which branch a store belongs to.
  const showsBranch = featureAccess.pricebookShopsAllBranches(role);
  const columns = showsBranch ? [...BASE_COLUMNS, BRANCH_COLUMN] : BASE_COLUMNS;

  const fetchCustomers = useCallback(
    async (page: number, limit: number, search?: string) => {
      const {
        data: { data },
      } = await getPricebookCustomers(
        priceBookUuid,
        page,
        limit,
        search,
        branchUuid,
      );
      const rows: PricebookCustomer[] = data.rows;
      const count: number = data.count;
      return { rows, count };
    },
    [priceBookUuid, branchUuid],
  );

  const {
    search,
    handleChangeSearch,
    page,
    setPage,
    limit,
    setLimit,
    rows: customers,
    totalCount,
    loading,
  } = useSearchablePagedRows({ open, fetchRows: fetchCustomers });

  const title = branchLabel ? `รายชื่อลูกค้า · ${branchLabel}` : "รายชื่อลูกค้า";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        component="div"
        sx={{ display: "flex", alignItems: "center", gap: 2 }}
      >
        <Typography variant="body1" fontWeight={600} sx={{ flexGrow: 1 }}>
          {title}{" "}
          <Typography component="span" variant="body2" color="text.secondary">
            (ทั้งหมด {numberWithCommas(totalCount)} รายการ)
          </Typography>
        </Typography>
        <IconButton size="small" aria-label="ปิด" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack gap={2}>
          <TextField
            size="small"
            fullWidth
            placeholder="ค้นหารหัสลูกค้า หรือชื่อลูกค้า"
            value={search}
            onChange={handleChangeSearch}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <TableContainer>
            <Table>
              <TableHead
                sx={{ bgcolor: (theme) => theme.palette.background.default }}
              >
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column}>{column}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <Skeleton
                    columns={columns.length}
                    rows={limit}
                    rowHeight={ROW_HEIGHT}
                  />
                )}
                {!loading && customers.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      <Typography color="text.secondary" fontWeight={500}>
                        ไม่พบลูกค้าที่ใช้สมุดราคานี้
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  customers.map((customer, index) => (
                    <CustomerRow
                      key={customer.uuid}
                      customer={customer}
                      striped={index % 2 === 1}
                      showsBranch={showsBranch}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <CataloguePagination
            totalCount={totalCount}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

interface CustomerRowProps {
  customer: PricebookCustomer;
  striped: boolean;
  showsBranch: boolean;
}

function CustomerRow({ customer, striped, showsBranch }: CustomerRowProps) {
  // A new tab so the pricebook the reader came from is still open behind it.
  const handleOpenCustomer = () => {
    window.open(
      customerDetailsPath(customer.uuid),
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <TableRow
      sx={{
        bgcolor: striped ? STRIPE_BACKGROUND : "#fff",
        height: ROW_HEIGHT,
        "& td, & th": { padding: `${CATALOGUE_CELL_PADDING}px` },
        cursor: "pointer",
      }}
      onClick={handleOpenCustomer}
    >
      <TableCell>{customer.customer_code || EMPTY_PLACEHOLDER}</TableCell>
      <TableCell>{customer.name || EMPTY_PLACEHOLDER}</TableCell>
      <TableCell>{customer.group_name || EMPTY_PLACEHOLDER}</TableCell>
      {showsBranch && <TableCell>{formatBranch(customer)}</TableCell>}
    </TableRow>
  );
}
